/* eslint-disable max-classes-per-file */

import { CONCEPTS } from '@rover/rsdk/src/concepts';
import { type ConsentPurpose, AnalyticsEvent } from '@rover/types/src/Analytics';

import type { LastUsedLoginMethod } from './lastUsedLoginMethod';

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires once per sign-in screen that renders a "Last used" badge, carrying the
 * method the badge points at. Pairs with `login-method-click` to answer whether
 * a badged user goes on to use the badged method.
 *
 * &nbsp;
 */
export class LastUsedBadgeShown extends AnalyticsEvent {
  eventName = 'last-used-badge-shown';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.people_and_pets.authentication;

  constructor(lastUsedMethod: LastUsedLoginMethod) {
    const properties = { last_used_method: lastUsedMethod };

    // Also sent as observability tags, which is what puts them on the Datadog
    // metric this event mirrors to. Safe to forward whole: the only field is a
    // `LastUsedLoginMethod`, so the tag has four values.
    super(properties, properties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user picks a sign-in method on a screen that is showing a badge.
 * `is_last_used_method` is the duplicate-account signal: false means the user
 * is signing in a different way than they did last time on this device.
 *
 * Pairs with `last-used-badge-shown`, which supplies the impressions this is a
 * ratio of. A screen with no badge emits neither.
 *
 * &nbsp;
 */
export class LoginMethodClick extends AnalyticsEvent {
  eventName = 'login-method-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.people_and_pets.authentication;

  constructor(method: LastUsedLoginMethod, badgedMethod: LastUsedLoginMethod) {
    const properties = {
      method,
      last_used_method: badgedMethod,
      is_last_used_method: badgedMethod === method,
    };

    // See `LastUsedBadgeShown`: the tags are what make `is_last_used_method`
    // groupable in Datadog, which is the whole ratio. Bounded the same way.
    super(properties, properties);
  }
}
